/**
 * RealisticWaterMaterial — the "hyper-realistic" third water type: a TSL node
 * material combining every technique a modern real-time water needs, on top
 * of the same Gerstner wave set the CPU height query uses.
 *
 * Technique sources (researched before implementation):
 *  - Gerstner displacement + analytic normals: GPU Gems ch. 1 (Finch 2004),
 *    https://developer.nvidia.com/gpugems/gpugems/part-i-natural-effects/chapter-1-effective-water-simulation-physical-models
 *  - Screen-space refraction + planar reflection node idiom: three.js r182
 *    Water2Mesh (examples/jsm/objects/Water2Mesh.js) — `reflector()` with an
 *    offset uvNode, `adaptiveSceneColor(viewportSafeUV(screenUV + offset))`.
 *    The reflector's mirror plane is its `target`'s local XY plane (normal
 *    +Z, ReflectorNode.updateBefore); three's examples rotate the MESH so a
 *    child target inherits an up normal — this sheet bakes the rotation into
 *    the geometry, so the target is pre-rotated here (see `reflectorTarget`).
 *  - Wavelength-dependent Beer–Lambert absorption (red extinguishes first)
 *    over the view-space water thickness from the depth buffer: Ben Sims,
 *    "Physically Plausible, Simplified Water Shading" (2021),
 *    https://www.ben-sims.com/2021/02/13/physically-plausible-simplified-water-shading/
 *  - Depth-difference shoreline foam + animated Voronoi caustics modulating
 *    the refracted floor: Cyanilux "Water Shader Breakdown"
 *    (https://www.cyanilux.com/tutorials/water-shader-breakdown/) and
 *    GPU Gems ch. 2 "Rendering Water Caustics" (aesthetics-driven approximation).
 *  - Schlick Fresnel with F0 ≈ 0.02 (water IOR 1.33).
 *  - Underside rendering: Snell's window — from below, rays within the
 *    critical angle (sin θc = 1/1.33 → θc ≈ 48.75°, cos θc ≈ 0.66) transmit
 *    the above-water scene; beyond it, total internal reflection shows only
 *    the dark water body (standard real-time approximation).
 *
 * The water-realism wave (2026-09-25) adds OPT-IN terms, each behind a
 * config field so an owner that omits it gets exactly the old look:
 *  - `envReflection` (R1): the sky side samples the sky PMREM along the
 *    wavy reflection vector at the FILTERED roughness; `setEnvMap` binds the
 *    bake whenever it lands (a placeholder until then → the flat tint).
 *    `cloudReflection`: the reflected ray meets the cloud deck's altitude
 *    plane and reads the cloud field's own coverage map there.
 *  - `scattering` (R3): the Atlas / Sea of Thieves crest + body BSDF as
 *    GodotOceanWaves writes it — a backlit-crest glow, a view-facing body
 *    term and a Lambert term, tinted, sun-coloured, weighted by (1 − F).
 *  - `glitterFilter` (R4): Bruneton 2010 — waves (and the detail maps) the
 *    pixel footprint cannot resolve leave the normal and their slope
 *    variance joins α²; the sun is Karis' 0.27° disc; the old ×6 clamp is a
 *    soft roll-off instead.
 *  - `farMode` (R5): the far-tier sea — no vertex stage, a flat macro normal,
 *    the full wave variance as roughness, the same colours / PMREM / body.
 *  - `calm` (R13): lake glass with wind "cat's-paw" patches.
 *  - `analyticBed` (R6): WebGPU shallows without a scene copy — the view ray
 *    refracts through the surface and meets a bed plane at `bedDepth`, whose
 *    albedo is sampled, sun-lit through the column, caustic-dappled and
 *    attenuated; the Snell's window looks up the refracted ray into the PMREM.
 * `waterLook.ts` holds every term's CPU oracle.
 *
 * The material is UNLIT by design (MeshBasicNodeMaterial): every lighting
 * term — sun specular, sky/reflection energy, subsurface tint — is authored
 * here, which is how the reference implementations behave and keeps the
 * result stable across the WebGPU and WebGL2 backends.
 */

import * as THREE from 'three/webgpu';
import {
  Discard,
  Fn,
  If,
  attribute,
  color,
  cameraPosition,
  clamp,
  cos,
  dot,
  exp,
  float,
  frontFacing,
  length,
  max,
  min,
  mix,
  sin,
  mx_noise_float,
  mx_worley_noise_float,
  normalize,
  abs,
  fwidth,
  refract,
  pmremTexture,
  pow,
  reflect,
  reflector,
  screenUV,
  smoothstep,
  texture,
  uniform,
  uv,
  vec2,
  vec3,
  vec4,
  viewportLinearDepth,
  viewportSafeUV,
  linearDepth,
  positionLocal,
  positionPrevious,
  positionWorld,
} from 'three/tsl';

import { adaptiveSceneColor } from './adaptiveViewportTexture.js';

import { filterVertexWaves, maxAmplitude, type GerstnerWave } from './GerstnerWaves.js';
import { makeTslGerstner, type TslWaveModulation } from './tsl-gerstner.js';
import { isWaveProvider, type WaveProvider } from './WaveProvider.js';
import { rainRingSlope } from './rain-rings.js';
import { DEFAULT_SCATTER, SUN_ANGULAR_RADIUS, totalSlopeVariance, type ScatterParams } from './waterLook.js';

/**
 * Owner-supplied sea-state uniforms (config.seaState as an object): the
 * owner creates them so a CausticsProjector built BEFORE the material can
 * share the same modulation nodes (its light map is bound at material build).
 */
export interface SeaStateUniforms {
  ampScale: ReturnType<typeof uniform<number>>;
  dirRot: ReturnType<typeof uniform<number>>;
}

export interface RealisticWaterMaterialConfig {
  /** Shallow-water tint (what thin water adds over the floor). */
  shallowColor: THREE.ColorRepresentation;
  /** Deep-water color (what the column converges to as light dies). */
  deepColor: THREE.ColorRepresentation;
  /**
   * Beer–Lambert extinction per channel, 1/world-unit. Water kills red first,
   * then green — a reddish extinction vector, so the residual light shifts
   * blue-green with depth (Sims 2021).
   */
  absorption: { r: number; g: number; b: number };
  /** Screen-space refraction distortion strength. */
  distortionScale: number;
  /**
   * Sample the live viewport copy for refraction / Snell's window (default
   * true). Set FALSE on the WebGPU backend: r182's copyFramebufferToTexture
   * ends + re-begins the active render pass per copy, and its per-context
   * encoder state goes stale across frames/resizes (already-ended passes,
   * finished encoders, destroyed attachments in submits → the setPipeline/
   * createBindGroup collapse, live-traced 2026-07-31). The fallback renders
   * depth-tinted transmission (shallow-color floor, flat-sky Snell window) —
   * no scene copy at all.
   */
  screenSpaceRefraction?: boolean;
  /** Foam color. */
  foamColor: THREE.ColorRepresentation;
  /** Water-thickness (view depth difference) covered by shoreline foam. */
  foamShoreDepth: number;
  /** Fraction of total crest amplitude where crest foam starts (0..1). */
  foamCrestStart: number;
  /**
   * Jacobian whitecap threshold: foam ramps in as the surface-compression
   * Jacobian falls below this (folding crests sit near/below 0). Default 0.6.
   */
  foamJacobianThreshold: number;
  /**
   * Persistent foam-trail texture (FoamAccumulator) sampled in plane UV.
   * Null/omitted = instantaneous Jacobian foam only.
   */
  foamAccumTexture?: THREE.Texture | null;
  /** Caustic shimmer intensity on the refracted floor (0 disables). */
  causticIntensity: number;
  /** Caustic pattern world scale. */
  causticScale: number;
  /**
   * Projected caustic light map (CausticsProjector, plane-UV addressed).
   * When set it replaces the Voronoi shimmer with the real refracted-ray
   * pattern.
   */
  causticTexture?: THREE.Texture | null;
  /** Planar reflections on/off (extra render pass when on). */
  reflection: boolean;
  /** Reflection render-target scale relative to the viewport. */
  reflectionResolutionScale: number;
  /** Sky color used instead of the reflector when reflection is off. */
  skyColor: THREE.ColorRepresentation;
  /** Sun specular color. */
  sunColor: THREE.ColorRepresentation;
  /** Blinn specular exponent for the sun glint (legacy; GGX path ignores it). */
  sunShininess: number;
  /**
   * Microfacet roughness for the GGX sun glint (0.02 mirror-calm … 0.3
   * churned). Governs the long grazing-angle glitter stretch. Default 0.12.
   */
  sunRoughness: number;
  /**
   * Environment map (PMREM-compatible) sampled for the sky side when planar
   * `reflection` is off — IBL instead of the flat skyColor. Null = flat color.
   */
  envMap?: THREE.Texture | null;
  /**
   * Interactive ripple field (RippleField texture: r=height, g=dh/dx,
   * b=dh/dz in plane UV) — adds displacement + normal detail on top of the
   * Gerstner waves. Null/omitted = no interactive ripples.
   */
  rippleTexture?: THREE.Texture | null;
  /**
   * Camera-following INTERACTION field (2026-09-11): a RippleField whose
   * origin follows the viewer, addressed in WORLD XZ over `size` metres
   * around `uInteractionCenter` (handles) — r = height, g = dh/dx,
   * b = dh/dz. Wakes, splashes, wading rings and rain live here; the plane-
   * UV `rippleTexture` stays for fixed-footprint ponds. Added AFTER the
   * shoaling attenuation (a wading ring in 30 cm of water must not die
   * with the swell), faded over the window's outer 10 %. Null = none.
   */
  interaction?: { texture: THREE.Texture; size: number } | null;
  /** Foam raised by steep interaction slopes (wakes, splashes); 0 = none. Default 0.6. */
  interactionFoam?: number;
  /**
   * Expose the runtime SEA-STATE uniforms (`uWaveAmpScale`, `uWaveDirRot`):
   * the wind scales the whole wave field and turns its heading with no
   * shader rebuild (tsl-gerstner's modulated evaluators; the CPU samplers
   * take the same `WaveModulation`). Default false: constants, folded.
   */
  seaState?: boolean | SeaStateUniforms;
  /**
   * Raindrop rings on the surface (rain-rings.ts): the `uRainIntensity`
   * uniform (handles) fades them in — 0 costs one uniform compare's worth of
   * shader (the rings still evaluate; keep it off on tiers that never rain
   * by omitting the flag). Default false.
   */
  rainRipples?: boolean;
  /**
   * FFT ocean textures (OceanFFT): when present the surface displaces from
   * the cascade textures instead of the analytic Gerstner sum —
   * displacement = (dx, h, dz), derivatives = (slopeX, slopeZ, Jacobian).
   */
  fft?: {
    displacement: THREE.Texture[];
    derivatives: THREE.Texture[];
    patchSizes: number[];
  } | null;
  /**
   * Infinite-ocean displacement fade: waves flatten between start and end
   * metres from the mesh center (the camera) so the sparse horizon grid
   * never aliases. Null = no fade (finite bodies).
   */
  distanceFade?: { start: number; end: number } | null;
  /**
   * Terrain-aware shoreline data (ShorelineField): bed depth below the calm
   * surface, plane-UV addressed. Enables wave SHOALING (displacement fades
   * to zero as the bed rises — swell dies on the beach), a shallow sand
   * tint, and a surf-foam band that works from any viewpoint (the
   * depth-buffer shore foam needs the floor on screen).
   */
  shoreline?: {
    /** R = bedDepth/encodeMax, plane-UV addressed (v flips like foam). */
    depthTexture?: THREE.Texture | null;
    /**
     * Bed depth from a per-vertex FLOAT attribute (metres of water under the
     * surface at the vertex) instead of the plane-UV bake — for finite strips
     * such as river ribbons, which have no bake footprint. Drives the same
     * shoaling, surf-foam band and shallow tint; `encodeMax` is ignored.
     */
    depthAttribute?: string;
    /**
     * Carve the surface along the zero-depth contour (the sea's waterline
     * discard). Default: on with a depth texture, OFF with an attribute — a
     * strip keeps its lip so the water still meets a coarse cut bank.
     */
    waterlineDiscard?: boolean;
    /** Metres encoded at R = 1. */
    encodeMax: number;
    /** Depth below which wave displacement attenuates. */
    shoalDepth: number;
    /** Bed depth covered by the surf-foam band. */
    foamDepth: number;
    /**
     * The surf band's WIDTH in metres from the waterline (surfaces agent, 2026-09-27). A depth band alone is 10–20 m wide
     * on a 2–3 % beach and drew as a solid white sheet over the shallows; with this set the band (and the depth-buffer
     * shore foam) also ends `foamBandM` metres out — the distance estimated per pixel as bedDepth ÷ its screen-space
     * gradient — and breaks into lace (the band no longer lifts the noise gate). Unset = the depth band alone.
     */
    foamBandM?: number;
    /** Shallow tint pulled in as the bed nears the surface (null = off). */
    shallowTint: THREE.ColorRepresentation | null;
  } | null;
  /**
   * Strip advection (rivers): the detail normals ride the mesh's own METRIC
   * uv (u = metres along the flow, v = metres across) and travel toward +u
   * at `speed` m/s — the pattern goes downstream, not with the wind. Null /
   * omitted = world-XZ detail (the sea). `uvScale` defaults to `normalScale`
   * so the texel density matches the sea's.
   */
  flow?: {
    speed: number;
    uvScale?: number;
    /**
     * R11 (2026-09-25): a vec2 vertex attribute packing (bed depth m, surface
     * speed m/s) — per-vertex Manning speeds in ONE buffer (WebGPU's 8-buffer
     * budget). Overrides `shoreline.depthAttribute` for the bed depth.
     */
    packedAttribute?: string;
    /** R11: two-phase flow advection (Vlachos 2010) at the per-vertex speed, noise-offset against the pulse. */
    twoPhase?: boolean;
    /** R11: white water from the Froude number (Fr > 0.7 streaks, Fr > 1 standing waves). */
    froude?: boolean;
  } | null;
  /** Detail normal-map (tiling water normals); repeats per world unit. */
  normalMap: THREE.Texture;
  normalScale: number;
  /** Detail normal scroll speed. */
  normalSpeed: number;
  /**
   * Shortest wavelength the VERTEX stage displaces, in metres (Gerstner
   * mode). A displacement grid folds every wavelength below its Nyquist
   * limit (2× the vertex spacing) into pseudo-random per-vertex phases —
   * each quad tilts independently and the sea reads as a checkerboard of
   * flat tiles. Set this to ~2.5× the mesh's vertex spacing so geometry
   * carries only the swell it can sample; the shorter chop still renders
   * through the per-pixel analytic normal, Jacobian foam and detail maps,
   * none of which alias on the mesh. 0/omitted = displace every wave
   * (correct for fine grids where spacing ≪ the shortest wavelength).
   */
  vertexWavelengthCutoff?: number;

  // ── Water-realism wave (2026-09-25): every field opt-in (omitted = the old look) ──
  /**
   * R1: reflect the SKY PMREM (the sky bake's `scene.environment`) instead of
   * the flat tint. The PMREM node is built at once around a placeholder;
   * `handles.setEnvMap(tex)` binds the bake whenever it exists (`uEnvWeight`
   * 0 → the flat tint, 1 → the PMREM). Ignored with a planar reflector.
   */
  envReflection?: boolean;
  /**
   * THE PHYSICAL SUN (2026-09-26, session 3): the three sun-driven terms — the
   * glint, the crest / body scattering and the analytic bed's direct light —
   * read `handles.uSunLight` (the key light's colour × its NORMAL illuminance,
   * relative to the authored reference) instead of the baked `sunColor` ×
   * `uIlluminance`. The owner writes it every frame from its light model, so the
   * sun road turns gold at sunset and follows the key's own curve while the
   * diffuse-lit terms keep `uIlluminance` (the horizontal global). Omitted =
   * the old look.
   */
  physicalSun?: boolean;
  /**
   * R1 follow-up: clouds in the reflection. The reflected ray meets a plane at
   * `altitude` metres (absolute y) and reads `texture` (R = coverage) there,
   * addressed like the cloud field: (xz + uCloudOffset) rotated by `rotation`
   * over `scaleM` metres per repeat. `setCloudTexture` swaps the map.
   */
  cloudReflection?: { texture?: THREE.Texture | null; scaleM: number; rotation: number; altitude: number; strength?: number } | null;
  /** R3: crest + body scattering (true = DEFAULT_SCATTER). */
  scattering?: boolean | (Partial<ScatterParams> & { bubbles?: number; bubbleColor?: [number, number, number] }) | null;
  /**
   * R4: distance-aware glitter. `normalMapVariance` is the detail map's own
   * mean-square slope (the spectrum bake reports it); `detailWavelength` the
   * detail layers' characteristic wavelength (m); `normalFadeDistance` the
   * e-folding distance of the detail normal strength (GodotOceanWaves).
   */
  glitterFilter?: boolean | { normalMapVariance?: number; detailWavelength?: number; normalFadeDistance?: number; sunAngularRadius?: number } | null;
  /**
   * R5: the FAR sea (a flat sheet past the near sea): no vertex stage, a flat
   * macro normal, every wave's variance in the roughness, no depth read
   * (the open sea reads deep). `nearSeaDiscard` carves the sheet under the
   * near sea's footprint (`uNearSeaRect`) — they would z-fight at a km.
   */
  farMode?: boolean | { nearSeaDiscard?: boolean } | null;
  /**
   * R13: a calm lake — perceptual roughness `roughness` on the glass and wind
   * "cat's-paw" patches (noise at `patchScale` 1/m drifting downwind at
   * `patchSpeed` m/s) where the ripples come back.
   */
  calm?: { roughness: number; patchScale: number; patchSpeed: number; glassFloor?: number } | null;
  /**
   * R6: the analytic bed. `albedoTexture` is plane-UV addressed like the
   * shoreline depth texture over `planeSize` metres (rgb albedo, linear);
   * without one the bed wears `albedoColor`. Lit by the sun through the
   * column (Beer–Lambert) with Worley caustics; replaces the flat floor of the
   * no-copy path (WebGPU) — the WebGL2 scene copy keeps real objects.
   */
  analyticBed?: { albedoTexture?: THREE.Texture | null; albedoColor?: THREE.ColorRepresentation; planeSize?: { x: number; z: number }; rippleScale?: number; ambient?: number } | null;
  /**
   * R10: the camera-centred GPU foam memory (FoamField): R = coverage, world XZ
   * over `size` metres around `uFoamCenter` (render space). Shaded from a
   * procedural foam pattern thresholded by the coverage; the CPU trail grid
   * (foamAccumTexture) stays as the far fallback outside the window.
   * `setFoamFieldTexture` swaps the ping-pong target each step.
   */
  foamField?: { texture: THREE.Texture; size: number } | null;
}

export const DEFAULT_REALISTIC_WATER: Omit<
  RealisticWaterMaterialConfig,
  'normalMap' | 'fft' | 'rippleTexture' | 'foamAccumTexture' | 'shoreline'
> = {
  shallowColor: 0x2e8b8b,
  deepColor: 0x03212e,
  absorption: { r: 0.9, g: 0.28, b: 0.16 },
  distortionScale: 0.045,
  foamColor: 0xeef6f8,
  foamShoreDepth: 0.55,
  foamCrestStart: 0.68,
  foamJacobianThreshold: 0.6,
  causticIntensity: 0.35,
  causticScale: 0.55,
  reflection: true,
  reflectionResolutionScale: 0.5,
  skyColor: 0x87a8c3,
  sunColor: 0xfff4e0,
  sunShininess: 220,
  sunRoughness: 0.12,
  normalScale: 0.16,
  normalSpeed: 0.06,
};

export interface RealisticWaterHandles {
  material: THREE.MeshBasicNodeMaterial;
  /** Drive this from the owning system — CPU height queries use the SAME clock. */
  uTime: { value: number };
  /**
   * Seconds since the previous frame. The vertex stage evaluates the wave
   * field at `uTime − uFrameDt` to emit `positionPrevious`, so motion blur and
   * temporal passes see the waves' REAL velocity instead of their whole
   * displacement. Defaults to 1/60; an owner that never sets it is merely
   * slightly off under a variable frame rate, never wrong.
   */
  uFrameDt: { value: number };
  uSunDirection: { value: THREE.Vector3 };
  /** Sea state (config.seaState): amplitude scale of the whole wave field (1 = authored). */
  uWaveAmpScale: { value: number };
  /** Sea state: rotation of every wave heading, radians. */
  uWaveDirRot: { value: number };
  /** The sea-state nodes for a CausticsProjector sharing this surface (null without seaState). */
  waveModulationNodes: TslWaveModulation | null;
  /** World XZ centre of the interaction field (config.interaction) — the RippleField's origin, in the mesh's world frame. */
  uInteractionCenter: { value: THREE.Vector2 };
  /** Jacobian whitecap threshold (initialised from config.foamJacobianThreshold; the wind raises it). */
  uFoamJacobian: { value: number };
  /** Detail-normal scroll direction × speed (default (1, 0.8)); point it downwind. */
  uDetailDrift: { value: THREE.Vector2 };
  /** Rain on the surface, 0..1 (config.rainRipples). */
  uRainIntensity: { value: number };
  /** Mesh world XZ offset so wave phase is world-anchored (sync on move). */
  uWorldOffset: { value: THREE.Vector2 };
  /**
   * Scene illumination 0..1 (1 = full day). Scales every AUTHORED radiance
   * term (deep scatter, flat sky fallback, foam, caustics, sun glint) so the
   * water follows the day/night cycle instead of glowing its daylight colors
   * at midnight. Drive from the sun elevation (WaterSystem does when a
   * DayNightCycle is present); keep a small floor (~0.04) for moonlit seas.
   */
  uIlluminance: { value: number };
  /**
   * Sky-side fallback tint (only sampled when `reflection` is off and no
   * `envMap` is set). Drive with the time-of-day sky tint so the sea
   * reflects sunsets/night instead of a constant day blue.
   */
  uSkyColor: { value: THREE.Color };
  /**
   * The key light for `physicalSun` (RGB, the sun's colour × its normal
   * illuminance relative to the authored reference — 1 ≈ the noon the look was
   * tuned under). Starts at `sunColor`; unused without `physicalSun`.
   */
  uSunLight: { value: THREE.Color };
  /**
   * Reflector target that MUST be added as a child of the water mesh (null
   * when `reflection` is off). It arrives PRE-ORIENTED: three's reflector
   * mirrors the scene across the plane through this object whose normal is
   * the object's LOCAL +Z, and this material is a horizontal sheet with the
   * rotation baked into the geometry (rotateX(-π/2)), so the target is
   * rotated −π/2 about X to make that +Z the mesh's +Y. Do not rotate it
   * again; move it (position.y) only when the surface is not at the mesh's
   * local y = 0 (per-vertex lake levels).
   */
  reflectorTarget: THREE.Object3D | null;
  /**
   * The reflector node itself (null when `reflection` is off). Diagnostics:
   * `reflectorNode.reflector.hasOutput` is false on any frame the camera
   * sat under the mirror plane (three clears the reflection to black then).
   */
  reflectorNode: ReturnType<typeof reflector> | null;
  // ── Water-realism wave (2026-09-25) ──
  /** Bind the sky PMREM (config.envReflection); null falls back to the flat tint. Cheap — a texture swap, no rebuild. */
  setEnvMap(tex: THREE.Texture | null): void;
  /** 0 = the flat tint, 1 = the PMREM (set by setEnvMap). */
  uEnvWeight: { value: number };
  /**
   * The PMREM's scale NOW relative to when it was baked (default 1). Owners copy
   * `scene.environmentIntensity` here every frame: a pre-exposed sky keeps the
   * cube at the scale it was baked at and carries the exposure curve between
   * bakes in `environmentIntensity` — lit materials get it from three, a node
   * that samples the cube directly does not (the sea popped at every re-bake).
   */
  uEnvIntensity: { value: number };
  /** Swap the cloud coverage map (config.cloudReflection). */
  setCloudTexture(tex: THREE.Texture | null): void;
  /** Cloud-field drift minus the rebase shift (the cloud-shadow drive's offset). */
  uCloudOffset: { value: THREE.Vector2 };
  /** Cloud reflection strength 0..1 (the global coverage). */
  uCloudStrength: { value: number };
  /** Scattering gain (config.scattering); 1 = the authored constants. */
  uScatterStrength: { value: number };
  /** Far mode: the near sea's footprint in render space (cx, cz, half-size, enabled 0/1). */
  uNearSeaRect: { value: THREE.Vector4 };
  /** R15: the planar reflection's share of the sky side (1 = planar, 0 = the PMREM) — with envReflection. */
  uPlanarWeight: { value: number };
  /**
   * R15: start / stop this body's planar reflection render (three's reflector
   * runs from its updateBefore — 'none' skips it at runtime, the texture stays
   * as last drawn). No-op without a reflector.
   */
  setPlanarActive(on: boolean): void;
  /** Is the planar reflection currently rendering? */
  readonly planarActive: boolean;
  /** R10: the foam field's centre in render space (FoamField.uCenter's value). */
  uFoamCenter: { value: THREE.Vector2 };
  /** R10: bind the foam field's newest target (per step). */
  setFoamFieldTexture(tex: THREE.Texture): void;
}

/**
 * R1's stand-in for the sky cube until the first bake lands (2026-09-25, session 2).
 *
 * three r185's PMREMNode reads `this._pmrem.isRenderTargetTexture` in SETUP. The
 * first stand-in was an image-less `Texture`, which never becomes a PMREM, so
 * `_pmrem` stayed null and every water material compiled before the sky's first
 * bake threw "Cannot read properties of null (reading 'isRenderTargetTexture')"
 * (8 TSL errors in the WebGL2 look probe). This stand-in IS a PMREM to the node:
 * cube-UV mapped (taken as-is, no generation), flagged as a render target like
 * the baked cube — the Y flip is decided at build time and must match the cube
 * swapped in later — and sized so the cube-UV maths is sane (three allocates a
 * render-target texture empty, as it does its own PMREMNode default). It is
 * sampled only under `uEnvWeight` 0, so it never shows. One for every material.
 */
let envPlaceholderShared: THREE.Texture | null = null;
export function envPlaceholderTexture(): THREE.Texture {
  if (!envPlaceholderShared) {
    const t = new THREE.Texture();
    t.name = 'WaterEnvPlaceholder';
    t.mapping = THREE.CubeUVReflectionMapping;
    (t as unknown as { isRenderTargetTexture: boolean }).isRenderTargetTexture = true;
    // 3·max(2^maxMip, 112) × 2^(maxMip+2) with maxMip 4: the PMREM layout's own proportions.
    (t as unknown as { image: { width: number; height: number } }).image = { width: 336, height: 64 };
    envPlaceholderShared = t;
  }
  return envPlaceholderShared;
}

/**
 * Build the node material for a wave set. The wave parameters are baked as
 * shader constants (they define the sea state; changing them rebuilds the
 * material) while time / sun / offset stay uniforms.
 */
export function createRealisticWaterMaterial(
  config: RealisticWaterMaterialConfig,
  wavesOrProvider: GerstnerWave[] | WaveProvider,
): RealisticWaterHandles {
  // Shader gaps R5 (2026-09-30): the waves may come as a WaveProvider. A Gerstner provider hands over its set and every
  // line below runs exactly as it always did; a non-Gerstner provider (the FFT patch) supplies the three evaluator sets.
  const waveProvider = isWaveProvider(wavesOrProvider) ? wavesOrProvider : null;
  const waves: GerstnerWave[] = waveProvider ? (waveProvider.waves ?? []) : (wavesOrProvider as GerstnerWave[]);
  const uTime = uniform(0);
  const uSunDirection = uniform(new THREE.Vector3(0.4, 0.8, 0.2).normalize());
  const uWorldOffset = uniform(new THREE.Vector2(0, 0));
  const uIlluminance = uniform(1);
  const uSkyColor = uniform(new THREE.Color(config.skyColor));
  const uSunLight = uniform(new THREE.Color(config.sunColor));
  // The sun-driven terms' light: the owner's key (physicalSun) or the old baked colour × the scalar day curve.
  const sunLightNode = () => (config.physicalSun ? vec3(uSunLight) : color(new THREE.Color(config.sunColor)).mul(uIlluminance)) as unknown as ReturnType<typeof vec3>;
  const ownSea = typeof config.seaState === 'object' && config.seaState !== null ? config.seaState : null;
  const uWaveAmpScale = ownSea ? ownSea.ampScale : uniform(1);
  const uWaveDirRot = ownSea ? ownSea.dirRot : uniform(0);
  const uFoamJacobian = uniform(config.foamJacobianThreshold);
  const uDetailDrift = uniform(new THREE.Vector2(1, 0.8));
  const uInteractionCenter = uniform(new THREE.Vector2(0, 0));
  const uRainIntensity = uniform(0);
  const uEnvWeight = uniform(0);
  const uEnvIntensity = uniform(1);
  const uCloudOffset = uniform(new THREE.Vector2(0, 0));
  const uCloudStrength = uniform(config.cloudReflection?.strength ?? 0.8);
  const uScatterStrength = uniform(1);
  const uNearSeaRect = uniform(new THREE.Vector4(0, 0, 0, 0));
  const uPlanarWeight = uniform(1);
  const uFoamCenter = uniform(new THREE.Vector2(0, 0));
  const foamFieldCfg = config.foamField ?? null;
  let foamFieldCurrent: THREE.Texture | null = foamFieldCfg?.texture ?? null;
  const foamFieldNodes: Array<{ value: THREE.Texture }> = [];
  const farCfg = config.farMode ? (typeof config.farMode === 'object' ? config.farMode : {}) : null;
  const glitterCfg = config.glitterFilter ? (typeof config.glitterFilter === 'object' ? config.glitterFilter : {}) : null;
  const scatterCfg = config.scattering ? { ...DEFAULT_SCATTER, bubbles: 0.35, bubbleColor: [0.55, 0.7, 0.68] as [number, number, number], ...(typeof config.scattering === 'object' ? config.scattering : {}) } : null;
  // Sea state: the same amplitude scale + heading rotation the CPU samplers
  // apply (GerstnerWaves.WaveModulation), as uniform nodes — cos/sin once.
  const modNodes: TslWaveModulation | null = config.seaState
    ? { ampScale: uWaveAmpScale as unknown as ReturnType<typeof float>, dirCos: cos(uWaveDirRot), dirSin: sin(uWaveDirRot) }
    : null;

  const uFrameDt = uniform(1 / 60); // (declared here so a provider's evaluators can take it — see the previous-frame evaluator below)
  const waveField = waveProvider && waveProvider.kind !== 'gerstner'
    ? waveProvider.tsl(uTime, uFrameDt, modNodes, config.vertexWavelengthCutoff ?? 0)
    : null;
  const totalAmp = Math.max(waveField ? waveProvider!.maxAmplitude() : maxAmplitude(waves), 1e-4);
  // The crest-foam band scales with the sea state (the authored total amplitude is a constant).
  const ampScaleNode = modNodes ? modNodes.ampScale : float(1);

  // ── Gerstner sum helpers (shared with the caustics projector) ────────────
  const gerstner = waveField ? waveField.surface : makeTslGerstner(waves, uTime, modNodes ?? undefined);
  // Vertex-stage wave set: drop wavelengths the grid cannot sample (they
  // alias into per-quad tile noise — see vertexWavelengthCutoff docs). The
  // FRAGMENT stages keep the FULL set: normals/foam/jacobian evaluate per
  // pixel, so short chop stays visible without touching the geometry.
  const vertexWaves = filterVertexWaves(waves, config.vertexWavelengthCutoff ?? 0);
  const gerstnerVertex = waveField ? waveField.vertex :
    vertexWaves.length === waves.length ? gerstner : makeTslGerstner(vertexWaves, uTime, modNodes ?? undefined);
  // PREVIOUS-FRAME evaluator: the same wave set on a time uniform one frame
  // behind, so the vertex stage can emit a true `positionPrevious`. Without it
  // three defaults positionPrevious to the UNDISPLACED geometry, making every
  // vertex's velocity equal to the WHOLE displacement — with motion blur or a
  // temporal pass on, a still camera smears the entire sea (the positionPrevious
  // LAW; the same defect HierarchicalWind fixed for foliage). 2026-09-15.
  // Owners only drive `uFrameDt` (seconds since the last frame); the previous
  // time is derived in the shader, so an owner that never sets it still gets a
  // sane 1/60 s rather than a bogus velocity.
  const gerstnerVertexPrev = waveField ? waveField.vertexPrevious : makeTslGerstner(vertexWaves, uTime.sub(uFrameDt) as never, modNodes ?? undefined);

  // Interaction window: world XZ → [0,1]² over the field, with a 10 % rim fade
  // so the window's edge never draws a seam (the field follows the viewer).
  const interaction = config.interaction ?? null;
  const interactionUv = (px: ReturnType<typeof float>, pz: ReturnType<typeof float>) =>
    vec2(px, pz).sub(uInteractionCenter).div(interaction!.size).add(0.5);
  const interactionEdge = (iuv: ReturnType<typeof interactionUv>) =>
    smoothstep(0, 0.1, min(min(iuv.x, float(1).sub(iuv.x)), min(iuv.y, float(1).sub(iuv.y))));

  const material = new THREE.MeshBasicNodeMaterial();

  // Bed depth (metres under the calm surface): the plane-UV bake, or a vertex
  // attribute for finite strips (river ribbons carry no bake footprint).
  const shore = config.shoreline ?? null;
  const bedDepthNode = (): ReturnType<typeof float> | null => {
    if (!shore) return null;
    if (config.flow?.packedAttribute) return attribute(config.flow.packedAttribute, 'vec2').x as unknown as ReturnType<typeof float>;
    if (shore.depthAttribute) return attribute(shore.depthAttribute, 'float') as ReturnType<typeof float>;
    if (shore.depthTexture) {
      return texture(shore.depthTexture, vec2(uv().x, float(1).sub(uv().y))).r.mul(shore.encodeMax);
    }
    return null;
  };

  // ── Vertex: displace the grid by the wave field (world-anchored phase),
  // plus the interactive ripple height (plane-UV texture; v flips because
  // rotateX(-π/2) maps local z = −y).
  // FFT mode: displacement/derivatives come from the cascade textures the
  // compute pipeline refreshes each frame (world-anchored UV per patch).
  const fft = config.fft ?? null;
  const fftDisplacement = (px: ReturnType<typeof float>, pz: ReturnType<typeof float>) => {
    let dx: ReturnType<typeof float> = float(0);
    let dy: ReturnType<typeof float> = float(0);
    let dz: ReturnType<typeof float> = float(0);
    for (let i = 0; i < (fft?.displacement.length ?? 0); i++) {
      const d = texture(fft!.displacement[i], vec2(px, pz).div(fft!.patchSizes[i]));
      dx = dx.add(d.x);
      dy = dy.add(d.y);
      dz = dz.add(d.z);
    }
    return { dx, dy, dz };
  };

  /**
   * The displaced vertex for one wave evaluator. Called TWICE — once with the
   * current wave field and once one frame back — so the vertex stage can emit
   * a true previous position for the velocity buffer.
   *
   * The ripple and interaction TEXTURES are deliberately excluded from the
   * previous evaluation: they have no history to sample, and their amplitude
   * is small next to the swell, so treating them as static costs a little
   * under-blur on ripples and avoids inventing motion.
   */
  const displacedAt = (g: typeof gerstnerVertex, withTextures: boolean) => {
    const wx = positionLocal.x.add(uWorldOffset.x);
    const wz = positionLocal.z.add(uWorldOffset.y);
    const d = fft ? fftDisplacement(wx, wz) : g.displacement(wx, wz);
    let dy = d.dy;
    if (config.rippleTexture && withTextures) {
      dy = dy.add(texture(config.rippleTexture, vec2(uv().x, float(1).sub(uv().y))).r);
    }
    let dx = d.dx;
    let dz = d.dz;
    const depthV = bedDepthNode();
    if (shore && depthV) {
      // Shoaling: the whole displacement (waves + ripples) dies as the bed
      // rises. MUST mirror ShorelineField.attenuationAt — the CPU height
      // query applies the same smoothstep so buoyancy matches the mesh.
      const shoal = smoothstep(0, shore.shoalDepth, depthV);
      dx = dx.mul(shoal);
      dy = dy.mul(shoal);
      dz = dz.mul(shoal);
    }
    if (config.distanceFade) {
      // Horizon fade: the exponential grid is sparse out there — displacing
      // it would alias. The mesh is camera-centered, so local radius = view
      // distance.
      const dist = positionLocal.x.mul(positionLocal.x).add(positionLocal.z.mul(positionLocal.z)).sqrt();
      const fade = float(1).sub(smoothstep(config.distanceFade.start, config.distanceFade.end, dist));
      dx = dx.mul(fade);
      dy = dy.mul(fade);
      dz = dz.mul(fade);
    }
    if (interaction && withTextures) {
      // Wakes and wading rings ride on top of the (shoaled) swell — never
      // attenuated by the bed: they are what shallow water shows most.
      const iuv = interactionUv(wx, wz);
      dy = dy.add(texture(interaction.texture, iuv).r.mul(interactionEdge(iuv)));
    }
    return vec3(positionLocal.x.add(dx), positionLocal.y.add(dy), positionLocal.z.add(dz));
  };

  // The FAR sea (R5) has no vertex stage: a flat sheet past the near sea's
  // displacement fade — no positionNode, so three's default positionPrevious
  // (the geometry) is exact.
  if (!farCfg) {
    material.positionNode = Fn(() => {
      // The positionPrevious LAW: a material with its own positionNode MUST
      // emit its own previous position, or three leaves it at the undisplaced
      // geometry and every wave's velocity becomes its full displacement.
      positionPrevious.assign(displacedAt(gerstnerVertexPrev, false) as never);
      return displacedAt(gerstnerVertex, true);
    })();
  }

  // ── Reflection sampler (optional) ────────────────────────────────────────
  let reflectionSampler: ReturnType<typeof reflector> | null = null;
  if (config.reflection && !farCfg) {
    // bounces:false — ONE scene render per body per FRAME. three's default
    // (bounces:true) re-renders on every render call AND lets a second
    // reflective body (lakes beside the sea) nest its own reflection render
    // inside ours: four extra scene renders a frame instead of two. A water
    // body never needs to show another water body's reflection.
    reflectionSampler = reflector({ bounces: false });
    reflectionSampler.reflector.resolutionScale = config.reflectionResolutionScale;
    // The reflector mirrors across the plane through its `target` whose
    // normal is the target's LOCAL +Z. This sheet's rotation is baked into
    // the geometry (rotateX(-π/2): local +Y is up), so an un-rotated target
    // under the mesh mirrors across a VERTICAL plane — the player saw their
    // own camera view pasted onto the sea, then black water the moment the
    // camera crossed to that plane's "facing away" side (2026-09-10). Orient
    // the target here so its +Z is the mesh's +Y; the owner still parents it
    // under the water mesh (`reflectorTarget`).
    reflectionSampler.target.rotation.x = -Math.PI / 2;
    reflectionSampler.target.name = 'WaterReflectorTarget';
  }

  // ── R1: the sky PMREM, built around a placeholder until the bake lands ──
  // A PMREMNode swaps its texture on `.value` (no rebuild): three's own
  // EnvironmentNode binds scene.environment the same way.
  // THE FN BODY RUNS PER BUILD (the main pass, a reflector's virtual camera …),
  // so every PMREM / cloud node it creates is collected and every swap updates
  // them all; a later build starts from the CURRENT texture.
  const envPlaceholder = envPlaceholderTexture();
  const envSource = config.envMap ?? null;
  const wantEnv = !!config.envReflection || !!envSource;
  let envCurrent: THREE.Texture = envSource ?? envPlaceholder;
  const envNodes: Array<{ value: THREE.Texture }> = [];
  const envSample = (dir: ReturnType<typeof vec3>, rough: ReturnType<typeof float>) => {
    const n = pmremTexture(envCurrent, dir, rough);
    envNodes.push(n as unknown as { value: THREE.Texture });
    return n;
  };
  if (envSource) uEnvWeight.value = 1;
  // ── R1 follow-up: the cloud coverage map (a 1×1 clear placeholder until bound) ──
  const cloudCfg = config.cloudReflection ?? null;
  const cloudPlaceholder = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1);
  cloudPlaceholder.needsUpdate = true;
  let cloudCurrent: THREE.Texture = cloudCfg?.texture ?? cloudPlaceholder;
  const cloudNodes: Array<{ value: THREE.Texture }> = [];
  // Total wave slope variance (farMode folds it all into α²).
  const waveVarTotal = waveField ? waveProvider!.totalSlopeVariance() : totalSlopeVariance(waves);

  // ── Fragment ─────────────────────────────────────────────────────────────
  material.colorNode = Fn(() => {
    const wx = positionWorld.x;
    const wz = positionWorld.z;

    // Macro normal from the wave field + two scrolling detail-normal samples.
    // FFT mode reads choppy-corrected slopes + Jacobian from the derivative
    // cascades; Gerstner mode evaluates the analytic sums.
    let fftSlopeX: ReturnType<typeof float> = float(0);
    let fftSlopeZ: ReturnType<typeof float> = float(0);
    let fftJacobian: ReturnType<typeof float> = float(1);
    if (fft) {
      for (let i = 0; i < fft.derivatives.length; i++) {
        const dv = texture(fft.derivatives[i], vec2(wx, wz).div(fft.patchSizes[i]));
        fftSlopeX = fftSlopeX.add(dv.x);
        fftSlopeZ = fftSlopeZ.add(dv.y);
        fftJacobian = fftJacobian.min(dv.z);
      }
    }
    // R4: the pixel footprint in metres (screen derivatives of world XZ — huge
    // at grazing angles, which is exactly where the waves must fold into BRDF).
    const footprint = glitterCfg || farCfg ? max(length(fwidth(vec2(wx, wz))), 1e-4) : null;
    let waveVariance: ReturnType<typeof float> = float(0);
    let macroNormal: ReturnType<typeof normalize>;
    if (fft) {
      macroNormal = normalize(vec3(fftSlopeX.negate(), 1, fftSlopeZ.negate()));
    } else if (farCfg) {
      // R5: nothing resolves at the horizon — a flat macro normal, every wave in α².
      macroNormal = normalize(vec3(0, 1, 0));
      waveVariance = ampScaleNode.mul(ampScaleNode).mul(waveVarTotal);
    } else if (glitterCfg && footprint) {
      const f = gerstner.normalFiltered(wx, wz, footprint);
      macroNormal = f.normal;
      waveVariance = f.variance;
    } else {
      macroNormal = gerstner.normal(wx, wz);
    }
    // R13: calm-lake glass with wind cat's-paws — a slow noise drifting
    // downwind decides where the ripples come back (0 glass … 1 rippled).
    let calmPatch: ReturnType<typeof float> | null = null;
    if (config.calm) {
      const cp = config.calm;
      const dl = max(length(uDetailDrift), 1e-3);
      const dir = uDetailDrift.div(dl);
      const drift = dir.mul(uTime.mul(cp.patchSpeed));
      const pn = mx_noise_float(vec3(wx.sub(drift.x).mul(cp.patchScale), wz.sub(drift.y).mul(cp.patchScale), uTime.mul(0.02)));
      const pn2 = mx_noise_float(vec3(wx.sub(drift.x).mul(cp.patchScale * 3.1).add(17.3), wz.sub(drift.y).mul(cp.patchScale * 3.1), uTime.mul(0.05)));
      calmPatch = smoothstep(0.1, 0.55, pn.mul(0.7).add(pn2.mul(0.3)).mul(0.5).add(0.5).sub(0.2));
      const keep = mix(float(cp.glassFloor ?? 0.2), float(1), calmPatch);
      macroNormal = normalize(mix(vec3(0, 1, 0), macroNormal, keep));
      waveVariance = waveVariance.mul(keep);
    }
    const t = uTime.mul(config.normalSpeed);
    // De-tiling: rotate the second sample ~34° so its repeat lattice never
    // lines up with the first — two axis-aligned lattices at different
    // scales still beat into a readable world-aligned quilt; a rotated one
    // cannot (standard layered-detail-normal practice).
    const rotC = Math.cos(0.6);
    const rotS = Math.sin(0.6);
    type Vec2Expr = ReturnType<ReturnType<typeof vec2>['add']>;
    let uv1: Vec2Expr;
    let uv2: Vec2Expr;
    // R11: the per-vertex surface speed (Manning) and the two-phase second layer.
    let flowSpeed: ReturnType<typeof float> | null = null;
    let uv1b: Vec2Expr | null = null;
    let uv2b: Vec2Expr | null = null;
    let phaseW: ReturnType<typeof float> | null = null;
    // The two-phase jitter, shared with the Froude streaks below (one noise, two cycles).
    let flowJitter: ReturnType<typeof float> | null = null;
    if (config.flow) {
      flowSpeed = (config.flow.packedAttribute ? attribute(config.flow.packedAttribute, 'vec2').y : float(config.flow.speed)) as ReturnType<typeof float>;
    }
    if (config.flow && config.flow.twoPhase && flowSpeed) {
      // Vlachos 2010: two copies of the detail advected by the LOCAL speed, half a
      // cycle apart; each resets while its weight is zero; a noise offset per
      // pixel keeps the resets from pulsing in unison across the river.
      const fs = config.flow.uvScale ?? config.normalScale;
      const cycle = 1.6;
      const jitter = mx_noise_float(vec3(uv().x.mul(0.07), uv().y.mul(0.45), 3.7)).mul(0.5);
      const ph0 = uTime.div(cycle).add(jitter).fract();
      const ph1 = ph0.add(0.5).fract();
      const fv = uv().y;
      uv1 = vec2(uv().x.sub(flowSpeed.mul(ph0).mul(cycle)), fv).mul(fs).add(vec2(0, t.mul(0.3)));
      uv1b = vec2(uv().x.sub(flowSpeed.mul(ph1).mul(cycle)), fv).mul(fs).add(vec2(0.37, t.mul(0.3).add(0.61)));
      phaseW = abs(ph0.mul(2).sub(1)); // 1 at A's reset → B carries the picture
      flowJitter = jitter as unknown as ReturnType<typeof float>;
      // The rotated second layer is PHASED too (2026-09-25, session 2). Advected by
      // uTime·speed with a PER-VERTEX speed it sheared without bound: Δspeed·t grows
      // every second (the banks run at ×0.4, Manning varies along the reach), so a
      // minute into a session the layer was stretched streaks and within minutes
      // aliased noise. Bounded offsets (≤ speed·cycle) are the point of two-phase.
      const fsp = flowSpeed;
      const rot2 = (ph: typeof ph0, dx: number, dz: number): Vec2Expr => {
        const fu = uv().x.sub(fsp.mul(ph).mul(cycle));
        return vec2(fu.mul(rotC).sub(fv.mul(rotS)), fu.mul(rotS).add(fv.mul(rotC)))
          .mul(fs * 2.7).add(vec2(t.negate().mul(0.2).add(dx), t.mul(0.5).add(dz))) as unknown as Vec2Expr;
      };
      uv2 = rot2(ph0, 0, 0);
      uv2b = rot2(ph1, 0.53, 0.19);
    } else if (config.flow) {
      // Strip advection: metric mesh uv, scrolled toward +u (downstream) at
      // flow.speed m/s; the second sample rotated + drifting a little across.
      const fs = config.flow.uvScale ?? config.normalScale;
      const fu = uv().x.sub(uTime.mul(config.flow.speed));
      const fv = uv().y;
      uv1 = vec2(fu, fv).mul(fs).add(vec2(0, t.mul(0.3)));
      const frx = fu.mul(rotC).sub(fv.mul(rotS));
      const frz = fu.mul(rotS).add(fv.mul(rotC));
      uv2 = vec2(frx, frz).mul(fs * 2.7).add(vec2(t.negate().mul(0.2), t.mul(0.5)));
    } else {
      // Both detail layers drift along uDetailDrift (the owner points it
      // downwind); the second a little turned and faster so the two lattices
      // never move in lockstep. Default (1, 0.8) = the authored look.
      uv1 = vec2(wx, wz).mul(config.normalScale).add(uDetailDrift.mul(t));
      const rx = wx.mul(rotC).sub(wz.mul(rotS));
      const rz = wx.mul(rotS).add(wz.mul(rotC));
      const drift2 = vec2(
        uDetailDrift.x.mul(0.9).sub(uDetailDrift.y.mul(0.3)),
        uDetailDrift.y.mul(0.9).add(uDetailDrift.x.mul(0.3)),
      ).mul(1.1);
      uv2 = vec2(rx, rz).mul(config.normalScale * 2.7).add(drift2.mul(t));
    }
    const n1 = uv1b && phaseW
      ? mix(texture(config.normalMap, uv1).xyz, texture(config.normalMap, uv1b).xyz, phaseW).mul(2).sub(1)
      : texture(config.normalMap, uv1).xyz.mul(2).sub(1);
    const n2 = uv2b && phaseW
      ? mix(texture(config.normalMap, uv2).xyz, texture(config.normalMap, uv2b).xyz, phaseW).mul(2).sub(1)
      : texture(config.normalMap, uv2).xyz.mul(2).sub(1);
    // Detail maps are tangent-space on an (almost) up-facing plane: XZ perturb.
    // The second sample was fetched in the rotated frame — rotate its
    // perturbation back (inverse rotation) so ripple directions stay unbiased.
    const n2x = n2.x.mul(rotC).add(n2.z.mul(rotS));
    const n2z = n2.x.negate().mul(rotS).add(n2.z.mul(rotC));
    const detail = n1.xz.add(vec2(n2x, n2z)).mul(0.5);
    // R4: the detail layers fold into roughness the same way once the pixel
    // footprint outgrows their chop, and fade with distance (GodotOceanWaves'
    // normal-strength falloff); the lost share of their variance joins α².
    let detailScale: ReturnType<typeof float> = float(0.35);
    let detailVariance: ReturnType<typeof float> = float(0);
    if ((glitterCfg || farCfg) && footprint) {
      const g = glitterCfg ?? {};
      const lam = g.detailWavelength ?? (1 / Math.max(1e-4, config.flow ? (config.flow.uvScale ?? config.normalScale) : config.normalScale)) / 6;
      const w1 = smoothstep(footprint.mul(2), footprint.mul(4), float(lam));
      const w2 = smoothstep(footprint.mul(2), footprint.mul(4), float(lam / 2.7));
      const dist = length(cameraPosition.sub(positionWorld));
      const fade = exp(dist.negate().div(g.normalFadeDistance ?? 900));
      const keep = w1.add(w2).mul(0.5).mul(fade);
      detailScale = keep.mul(0.35);
      const mapVar = g.normalMapVariance ?? 0.25;
      detailVariance = float(1).sub(keep).mul(mapVar * 0.35 * 0.35 * 0.5);
    }
    if (calmPatch) detailScale = detailScale.mul(mix(float(config.calm!.glassFloor ?? 0.2), float(1), calmPatch));
    // R11: fast water is rougher (normal strength by speed, Vlachos), and past a
    // Froude number of ~1 the standing waves chop it further.
    let froude: ReturnType<typeof float> | null = null;
    if (config.flow && flowSpeed && (config.flow.twoPhase || config.flow.froude)) {
      detailScale = detailScale.mul(clamp(flowSpeed.div(1.1).mul(0.45).add(0.55), 0.6, 1.9));
      if (config.flow.froude) {
        const bd = bedDepthNode() ?? float(1);
        froude = flowSpeed.div(max(bd, 0.05).mul(9.81).sqrt());
        detailScale = detailScale.mul(float(1).add(smoothstep(1.0, 1.6, froude).mul(0.8)));
      }
    }
    // Interactive ripples: gradient channels tilt the normal (linearized
    // n = (−dh/dx, 1, −dh/dz)).
    const rippleGrad = (config.rippleTexture
      ? texture(config.rippleTexture, vec2(uv().x, float(1).sub(uv().y))).gb
      : vec2(0, 0)) as ReturnType<typeof vec2>;
    // The camera-following interaction field: its gradient tilts the normal
    // the same way; its steepness raises foam (a wake's churn) below.
    let interGrad = vec2(0, 0).mul(1);
    if (interaction) {
      const iuv = interactionUv(wx, wz);
      interGrad = texture(interaction.texture, iuv).gb.mul(interactionEdge(iuv));
    }
    // Rain: Lagarde's growing rings, dense over the whole surface (the sim
    // field only carries the drops big enough to ring outward).
    const rain = config.rainRipples ? rainRingSlope(vec2(wx, wz), uRainIntensity as unknown as ReturnType<typeof float>) : null;
    // `.toVar` (THE LAZY-VAR LAW): the gated analytic bed below reads the normal inside an If; a node first built there is
    // declared in that block and undefined for every use after it. Assigned here, at the top, it is one value everywhere.
    const normal = normalize(vec3(
      macroNormal.x.add(detail.x.mul(detailScale)).sub(rippleGrad.x).sub(interGrad.x).sub(rain ? rain.x : float(0)),
      macroNormal.y,
      macroNormal.z.add(detail.y.mul(detailScale)).sub(rippleGrad.y).sub(interGrad.y).sub(rain ? rain.z : float(0)),
    )).toVar('wNormal');

    const viewDir = normalize(cameraPosition.sub(positionWorld));

    // Schlick Fresnel, F0 = 0.02 (water IOR 1.33).
    const cosTheta = max(dot(normal, viewDir), 0);
    const fresnel = float(0.02).add(pow(float(1).sub(cosTheta), 5).mul(0.98));

    // Terrain-aware bed depth from the baked ShorelineField (plane UV,
    // v flipped like the foam textures). Read EARLY: it also hardens the
    // water-thickness estimate below.
    const bedDepthRaw = bedDepthNode();
    const bedDepth = bedDepthRaw ? bedDepthRaw.toVar('wBedDepth') : null; // read inside the gated bed and after it (LAZY-VAR)
    // The surf band's distance from the waterline (surfaces, 2026-09-27; `shoreline.foamBandM`): bedDepth ÷ its horizontal
    // gradient, the gradient from screen derivatives — ASSIGNED here, once, in uniform flow (THE LAZY-VAR LAW: a derivative
    // first used inside a branch is undefined across a divergent quad) and before the waterline Discard.
    const bandM = shore?.foamBandM ?? 0;
    const bandDist = bedDepth && bandM > 0 ? float(1e4).toVar('wBandDist') : null;
    if (bandDist && bedDepth) {
      const slope = fwidth(bedDepth).div(max(fwidth(wx).add(fwidth(wz)), 1e-4));
      bandDist.assign(bedDepth.div(max(slope, 0.002)));
    }
    const bandK = bandDist ? float(1).sub(smoothstep(bandM * 0.5, bandM, bandDist)) : float(1);

    // Waterline discard: the depth-grid geometry interpolates surface height
    // across its edge quads, so on steep shores the sheet's rim hangs over
    // the ground — carve the surface along the BILINEAR zero-depth contour
    // (~5 cm of column) instead. A fragment Discard INSIDE the color node:
    // material.alphaTest would move the water into the alpha-tested pass
    // family, where its scene-copy node ran against mismatched render-target
    // formats on WebGPU (rgba16float vs bgra8unorm copy spam → destroyed-
    // texture cascade, 2026-07-30 console).
    // A strip (attribute depth) keeps its lip unless asked to carve.
    const waterlineDiscard = shore?.waterlineDiscard ?? !shore?.depthAttribute;
    if (bedDepth && waterlineDiscard) Discard(bedDepth.lessThan(0.05));
    // R5: the far sheet steps aside under the near sea's footprint (a 0.6 m
    // gap at a kilometre is inside the depth buffer's resolution — z-fight).
    if (farCfg && farCfg.nearSeaDiscard !== false) {
      const dd = abs(vec2(wx, wz).sub(uNearSeaRect.xy));
      Discard(max(dd.x, dd.y).lessThan(uNearSeaRect.z).and(uNearSeaRect.w.greaterThan(0.5)));
    }

    // View-space water thickness from the depth buffer: scene behind the
    // surface minus this fragment (Sims 2021 / Cyanilux depth-fade).
    // WHY the geometric term: the depth-buffer difference silently reads ~0
    // whenever the floor isn't in the rasterized viewport copy (off-screen
    // floor, backend depth-copy limits) — absorption then never absorbs, the
    // shoreline foam band covers the WHOLE surface, and caustics never
    // attenuate with depth. When the baked bed field exists, the slant path
    // length through the column (bedDepth / cosθ) is a floor-independent
    // lower bound — take the max of both estimates.
    // The far sea reads no depth: open water past the near sheet is deep.
    const depthThickness = farCfg ? float(1e4) : max(viewportLinearDepth.sub(linearDepth()), 0);
    const thickness = bedDepth
      ? max(depthThickness, bedDepth.div(max(cosTheta, 0.25)))
      : depthThickness;

    // Screen-space refraction — distortion eases to zero in shallow water so
    // the shoreline doesn't smear (Water2Mesh idiom + depth gate).
    const shallowEase = smoothstep(0, 0.35, thickness);
    const distortion = normal.xz.mul(config.distortionScale).mul(shallowEase);
    // No-copy fallback: the floor reads as sun-lit shallow color — absorption
    // below still converges it to deepColor with the column, so the sheet
    // keeps the classic depth gradient without ever touching the framebuffer.
    // R6 — the ANALYTIC BED: refract the view ray through the surface, meet a
    // bed plane `bedDepth` under it, sample the bed's albedo there, light it by
    // the sun through the column (+ caustics) — shallows on the no-copy path.
    const bedCfg = config.analyticBed ?? null;
    let bedLit: ReturnType<typeof vec3> | null = null;
    if (bedCfg && bedDepth) {
      const buildBed = (): ReturnType<typeof vec3> => {
      const inc = normalize(positionWorld.sub(cameraPosition));
      const tr = refract(inc, normal, 1 / 1.33);
      const down = max(tr.y.negate(), 0.08);
      const run = bedDepth.div(down);
      const off = tr.xz.mul(run);
      let alb: ReturnType<typeof vec3>;
      if (bedCfg.albedoTexture && bedCfg.planeSize) {
        const buv = vec2(uv().x.add(off.x.div(bedCfg.planeSize.x)), float(1).sub(uv().y).add(off.y.div(bedCfg.planeSize.z)));
        alb = texture(bedCfg.albedoTexture, buv).rgb as unknown as ReturnType<typeof vec3>;
      } else {
        alb = color(new THREE.Color(bedCfg.albedoColor ?? 0x8a7a5c)) as unknown as ReturnType<typeof vec3>;
      }
      const bx = wx.add(off.x), bz = wz.add(off.y);
      const rs = bedCfg.rippleScale ?? 1;
      // Sand ripples: noise stretched across one axis (wave-formed bedforms).
      const rip = mx_noise_float(vec3(bx.mul(0.9 * rs), bz.mul(2.9 * rs), 0)).mul(0.5).add(0.5);
      alb = alb.mul(mix(float(0.8), float(1.12), rip)) as unknown as typeof alb;
      const sunY = max(uSunDirection.y, 0);
      const sigB = vec3(config.absorption.r, config.absorption.g, config.absorption.b);
      const sunDown = exp(sigB.negate().mul(bedDepth.div(max(uSunDirection.y, 0.2))));
      // Worley caustics on the bed, projected along the sun's slant.
      const slant = uSunDirection.xz.div(max(uSunDirection.y, 0.2)).mul(bedDepth);
      const cB = vec3(bx.add(slant.x).mul(config.causticScale), bz.add(slant.y).mul(config.causticScale), uTime.mul(0.35));
      const cv1 = mx_worley_noise_float(cB);
      const cv2 = mx_worley_noise_float(cB.mul(1.9).add(vec3(3.1, 5.7, 0)));
      const causticB = pow(float(1).sub(cv1.mul(cv2)), 6).mul(config.causticIntensity * 4);
      // physicalSun: the owner's key (colour × normal illuminance) — the day curve is already in it.
      const sunCol = config.physicalSun ? vec3(uSunLight) : color(new THREE.Color(config.sunColor));
      const direct = sunCol.mul(sunY).mul(sunDown).mul(float(1).add(causticB.mul(smoothstep(0, 0.4, bedDepth))));
      const amb = vec3(uSkyColor).mul(bedCfg.ambient ?? 0.35).mul(exp(sigB.negate().mul(bedDepth)));
      return (config.physicalSun
        ? alb.mul(direct.add(amb.mul(uIlluminance)))
        : alb.mul(direct.add(amb)).mul(uIlluminance)) as unknown as ReturnType<typeof vec3>;
      };
      // WHY the depth gate: everything the bed adds reaches the eye through the sun's path down AND the view's path up
      // (both ≥ the depth), so it is bounded by e^(−2·σ_min·d) of its own scale — under 1e-7 past
      // ln(1e7) / (2·σ_min) (50 m at the default 0.16). The deep open sea is most of the screen, and the bed costs a
      // refracted ray, a noise and TWO Worley caustic fields (~4,000 ALU) per pixel there for nothing. Only on the no-copy
      // path (the bed IS the refracted scene there) and without an albedo map (a texture sampled in a branch has no mip).
      const sigMin = Math.max(0.01, Math.min(config.absorption.r, config.absorption.g, config.absorption.b));
      const gateM = Math.max(Math.log(1e7) / (2 * sigMin), (shore?.shoalDepth ?? 0) * 0.45 + 1);
      if (config.screenSpaceRefraction === false && !(bedCfg.albedoTexture && bedCfg.planeSize)) {
        const bedV = vec3(0, 0, 0).toVar('wBedLit');
        If(bedDepth.lessThan(gateM), () => { bedV.assign(buildBed()); });
        bedLit = bedV as unknown as ReturnType<typeof vec3>;
      } else {
        bedLit = buildBed();
      }
    }
    const refractedScene = config.screenSpaceRefraction !== false
      ? adaptiveSceneColor(viewportSafeUV(screenUV.add(distortion))).rgb
      : bedLit ?? color(new THREE.Color(config.shallowColor)).mul(uIlluminance);

    // Beer–Lambert per-channel transmittance over the water column;
    // reddish extinction ⇒ light goes teal→deep blue with depth.
    const sigma = vec3(config.absorption.r, config.absorption.g, config.absorption.b);
    const transmittance = exp(sigma.negate().mul(thickness));

    // Caustics on the refracted floor: the PROJECTED light map when the
    // CausticsProjector runs (real refracted-ray convergence), else the
    // animated Voronoi approximation (GPU Gems ch.2 style).
    const caustic = config.causticTexture
      ? texture(config.causticTexture, vec2(uv().x, float(1).sub(uv().y))).r.mul(config.causticIntensity)
      : (() => {
          const cUv = vec3(wx.mul(config.causticScale), wz.mul(config.causticScale), uTime.mul(0.35));
          const v1 = mx_worley_noise_float(cUv);
          const v2 = mx_worley_noise_float(cUv.mul(1.9).add(vec3(3.1, 5.7, 0)));
          return pow(float(1).sub(v1.mul(v2)), 6).mul(config.causticIntensity);
        })();
    const floorLit = bedLit && config.screenSpaceRefraction === false
      ? refractedScene
      : refractedScene.add(caustic.mul(uIlluminance).mul(transmittance.g).mul(shallowEase));

    // Underwater color: floor light absorbed by the column, converging to
    // deepColor; a shallow tint scatters in near the top of the column.
    // The AUTHORED radiance terms (deep scatter, flat sky, foam, caustics,
    // glint) scale by uIlluminance so the water follows the day/night cycle —
    // static day values read as a glowing sea at midnight (the same
    // daylight-authored-constant family as the fog and cloud night bugs).
    const shallow = color(new THREE.Color(config.shallowColor));
    const deep = color(new THREE.Color(config.deepColor)).mul(uIlluminance);
    let underwater = mix(deep, floorLit.mul(mix(shallow, vec3(1), transmittance)), transmittance);

    if (config.shoreline && bedDepth && config.shoreline.shallowTint !== null) {
      // Sand shows through as the bed nears the surface — view-independent
      // (the depth-buffer term above needs the floor rasterized on screen).
      const sand = color(new THREE.Color(config.shoreline.shallowTint));
      // Narrower + weaker than the original (·0.6 span, 0.65 mix): on wide
      // gentle beaches the old values painted the whole surf zone as flat
      // DRY sand at grazing angles instead of water over sand.
      const sandBlend = float(1).sub(smoothstep(0, config.shoreline.shoalDepth * 0.45, bedDepth));
      underwater = mix(underwater, floorLit.mul(sand), sandBlend.mul(0.45)) as typeof underwater;
    }

    // ── R4: the filtered roughness. α₀ is the authored glint roughness (α = r²,
    // the pre-wave convention), α² gains the slope variance of every wave and
    // detail layer the footprint folded out of the normal (Bruneton 2010).
    const alpha0Const = Math.max(0.02, config.sunRoughness) ** 2;
    let alpha0: ReturnType<typeof float> = float(alpha0Const);
    if (config.calm && calmPatch) {
      alpha0 = mix(float(Math.max(0.005, config.calm.roughness) ** 2), float(alpha0Const), calmPatch);
    }
    const filtered = !!(glitterCfg || farCfg);
    const alphaEff = filtered
      ? alpha0.mul(alpha0).add(waveVariance).add(detailVariance).sqrt().min(1)
      : alpha0;
    // Perceptual roughness for the PMREM mip (α = r²).
    const envRough = filtered ? alphaEff.sqrt().clamp(0.02, 0.95) : float(config.sunRoughness);

    // Sky/reflection side of the Fresnel blend.
    let skySide;
    // R1 — IBL: the sky PMREM sampled along the wavy reflection vector,
    // blurred by the FILTERED roughness (a far pixel reflects a softer sky).
    // Until the bake is bound (`uEnvWeight` 0) the flat tint stands in; at
    // night the PMREM (sun below the horizon, no stars in the bake) keeps
    // a floor of the night tint so the sea never reflects pure black.
    const envSide = () => {
      // THE HORIZON CLAMP (session 3, 2026-09-26): a wave face tilted toward a grazing camera reflects BELOW the
      // horizon, where the sky bake holds its ground (tan by day, rust at sunset) — the WebGPU look shots showed
      // stripes of "sand" along the far crests of the open sea. That ray would meet the sea again, not land: sample
      // the sky just above the horizon instead (the cloud reflection below clamps the same way).
      const rRaw = reflect(viewDir.negate(), normal);
      const reflectDir = normalize(vec3(rRaw.x, max(rRaw.y, 0.02), rRaw.z));
      const env = envSample(reflectDir as never, envRough as never).rgb.mul(uEnvIntensity);
      const flat = vec3(uSkyColor).mul(uIlluminance);
      return mix(flat, max(env, flat.mul(0.35)), uEnvWeight);
    };
    if (reflectionSampler) {
      // Perturb the planar-reflection lookup by the surface normal
      // (Water2Mesh idiom: uvNode is pre-populated by reflector()).
      if (reflectionSampler.uvNode) {
        reflectionSampler.uvNode = reflectionSampler.uvNode.add(normal.xz.mul(0.06));
      }
      // R15 — the reflection BUDGET: with the sky PMREM available, an inactive
      // planar body (`setPlanarActive(false)` stops its render) blends to it.
      skySide = wantEnv ? mix(envSide(), reflectionSampler.rgb, uPlanarWeight) : reflectionSampler.rgb;
    } else if (wantEnv) {
      skySide = envSide();
    } else {
      // Flat fallback: a UNIFORM (not a baked constant) so the owner can tint
      // it with the time of day — a static day-blue reflection side makes the
      // sea ignore sunsets and glow at night. Illumination-scaled like every
      // authored radiance term (reflector/envMap paths self-dim).
      skySide = vec3(uSkyColor).mul(uIlluminance);
    }
    if (cloudCfg) {
      // Clouds in the reflection: the reflected ray meets the deck plane and
      // reads the cloud field's own coverage there (the cloud-shadow addressing).
      const rr = normalize(reflect(viewDir.negate(), normal));
      const ry = max(rr.y, 0.02);
      const tHit = float(cloudCfg.altitude).sub(positionWorld.y).max(10).div(ry);
      const hit = vec2(wx, wz).add(rr.xz.mul(tHit)).add(uCloudOffset);
      const cr = Math.cos(cloudCfg.rotation), sr = Math.sin(cloudCfg.rotation);
      const cuv = vec2(hit.x.mul(cr).sub(hit.y.mul(sr)), hit.x.mul(sr).add(hit.y.mul(cr))).div(cloudCfg.scaleM);
      const covNode = texture(cloudCurrent, cuv);
      cloudNodes.push(covNode as unknown as { value: THREE.Texture });
      const cov = covNode.r;
      const fade = smoothstep(0.02, 0.14, rr.y).mul(exp(tHit.negate().div(24000)));
      const lit = smoothstep(-0.05, 0.35, uSunDirection.y);
      const cloudCol = mix(vec3(0.34, 0.37, 0.43), vec3(0.95, 0.93, 0.9), lit).mul(uIlluminance);
      skySide = mix(skySide, cloudCol, smoothstep(0.38, 0.78, cov).mul(fade).mul(uCloudStrength));
    }

    // Sun glint — GGX microfacet (Cook–Torrance): D·G·F/(4·NV·NL)·NL.
    // The GGX tail is what stretches sun glitter at grazing angles; Blinn
    // pow() cuts it off unnaturally early on rough water.
    // R4: with the filter on, α is the per-pixel filtered α, the sun is a 0.27°
    // DISC (Karis 2013: the light direction moves to the disc's closest point
    // to the reflection ray; the NDF keeps its energy with (α/α′)²).
    let sunL: ReturnType<typeof normalize> = normalize(uSunDirection);
    let alphaG: ReturnType<typeof float> = alphaEff;
    let karisNorm: ReturnType<typeof float> = float(1);
    if (filtered) {
      const theta = (glitterCfg && glitterCfg.sunAngularRadius) || SUN_ANGULAR_RADIUS;
      const rv = normalize(reflect(viewDir.negate(), normal));
      const toRay = rv.mul(dot(uSunDirection, rv)).sub(uSunDirection);
      const lr = max(length(toRay), 1e-5);
      sunL = normalize(uSunDirection.add(toRay.mul(min(float(Math.sin(theta)).div(lr), 1))));
      alphaG = alphaEff.add(theta * 0.5).min(1);
      karisNorm = alphaEff.div(alphaG).pow(2);
    }
    const halfway = normalize(sunL.add(viewDir));
    const ndh = max(dot(normal, halfway), 0);
    const ndv = max(dot(normal, viewDir), 1e-4);
    const ndl = max(dot(normal, sunL), 1e-4);
    const a2 = alphaG.mul(alphaG);
    const denom = ndh.mul(ndh).mul(a2.sub(1)).add(1);
    const distGgx = a2.div(denom.mul(denom).mul(Math.PI)).mul(karisNorm);
    const kGeom = alphaG.mul(0.5);
    const gView = ndv.div(ndv.mul(float(1).sub(kGeom)).add(kGeom));
    const gLight = ndl.div(ndl.mul(float(1).sub(kGeom)).add(kGeom));
    const fSpec = float(0.02).add(pow(float(1).sub(max(dot(halfway, viewDir), 0)), 5).mul(0.98));
    const glintRaw = distGgx
      .mul(gView)
      .mul(gLight)
      .mul(fSpec)
      .div(ndv.mul(ndl).mul(4).max(1e-4))
      .mul(ndl)
      .mul(sunLightNode())
      .mul(2.2)
      // The sun below the horizon casts no glint (the disc widening would
      // otherwise let a sun a hair under the horizon still sparkle).
      .mul(smoothstep(-0.02, 0.03, uSunDirection.y));
    // Firefly guard. Unfiltered: the old ×6 clamp (choppy per-pixel normals at
    // steep views spike GGX into saturated dashes). Filtered (R4): the waves
    // that would alias are already in α, so the clamp goes — a soft roll-off
    // (never a knee) keeps a mirror-calm sun disc HDR-bright for bloom
    // without an infinity.
    const glint = filtered
      ? glintRaw.div(glintRaw.div(40).add(1))
      : glintRaw.min(float(6));

    // Foam: shoreline band (thin water) + Jacobian whitecaps (surface
    // compression at folding crests) + lingering trails from the accumulator,
    // plus the legacy crest-height term, all broken up by animated noise.
    const shoreFoam = float(1).sub(smoothstep(0, config.foamShoreDepth, thickness)).mul(bandK); // × the band's width (foamBandM)
    const foamNoise = mx_noise_float(vec3(wx.mul(1.4), wz.mul(1.4), uTime.mul(0.5))).mul(0.5).add(0.5);
    // One displacement evaluation serves the crest foam AND the scattering's crest height.
    const dispFrag = fft || farCfg ? null : gerstner.displacement(wx, wz);
    const crestFoam = !dispFrag
      ? float(0) // the Jacobian term below carries FFT whitecaps (the far sea: none)
      : smoothstep(
          ampScaleNode.mul(config.foamCrestStart * totalAmp),
          ampScaleNode.mul(totalAmp * 0.98),
          dispFrag.dy,
        );
    // The whitecap threshold is a uniform: a gale raises it (more crests
    // break), a calm lowers it — initialised from the authored constant.
    const jThr = uFoamJacobian;
    // One Jacobian evaluation serves the whitecaps AND the scattering's crest peak mask (§3.4, surfaces 2026-09-27).
    const jacFrag = farCfg ? float(1) : (fft ? fftJacobian : gerstner.jacobian(wx, wz));
    // Ramps 0 → 1 as J falls from the threshold toward folding.
    const jacobianFoam = farCfg ? float(0) : float(1).sub(
      smoothstep(jThr.sub(0.35), jThr, jacFrag),
    );
    // Churn foam from the interaction field's steep slopes (a hull's wake,
    // a splash crown) — wading rings are too gentle to whiten.
    const wakeFoam = interaction
      ? smoothstep(0.06, 0.32, length(interGrad)).mul(config.interactionFoam ?? 0.6)
      : float(0);
    // Accumulated trails live in plane UV; v flips (plane rotateX maps z = −y).
    // pow-shaping thins the accumulator's coarse (metres-scale) texels into
    // froth instead of solid cotton blobs — the grid is CPU-priced, so its
    // resolution can't simply be raised.
    let trailFoam = config.foamAccumTexture
      ? pow(texture(config.foamAccumTexture, vec2(uv().x, float(1).sub(uv().y))).r, 1.5)
      : float(0);
    // R10: the camera-centred GPU foam memory, shaded from a procedural foam
    // pattern thresholded by its coverage (foam is lace with holes, never a flat
    // wash); the CPU trail grid only answers outside the window.
    let fieldFoam: ReturnType<typeof float> = float(0);
    if (foamFieldCfg && foamFieldCurrent) {
      const fuv = vec2(wx, wz).sub(uFoamCenter).div(foamFieldCfg.size).add(0.5);
      const fEdge = smoothstep(0, 0.1, min(min(fuv.x, float(1).sub(fuv.x)), min(fuv.y, float(1).sub(fuv.y))));
      const fNode = texture(foamFieldCurrent, fuv);
      foamFieldNodes.push(fNode as unknown as { value: THREE.Texture });
      const cov = clamp(max(fNode.r, fNode.g).mul(fEdge), 0, 1).toVar('wFoamCov'); // R whitecaps, G wakes (FoamField.wakeDecayS, surfaces 2026-09-27)
      // WHY gated: the lace is multiplied by smoothstep(0.03, 0.18, cov), which is exactly 0 at cov ≤ 0.03 — most of the
      // sea carries no foam memory, and its two Worley fields (~4,000 ALU) were evaluated there to be multiplied by 0.
      // The same expression inside the branch: the result is identical.
      const lace = float(0).toVar('wFoamLace');
      If(cov.greaterThan(0.03), () => {
        const fp = vec3(wx.mul(1.6), wz.mul(1.6), uTime.mul(0.07));
        const pattern = float(1).sub(mx_worley_noise_float(fp).mul(0.65).add(mx_worley_noise_float(fp.mul(2.3).add(vec3(1.7, 4.1, 0))).mul(0.35)));
        const thr = float(1).sub(cov);
        lace.assign(max(float(0), smoothstep(thr.sub(0.12), thr.add(0.04), pattern).mul(smoothstep(0.03, 0.18, cov))));
      });
      fieldFoam = lace as unknown as ReturnType<typeof float>;
      trailFoam = max(float(0), trailFoam.mul(float(1).sub(fEdge)));
    }
    // R11: white water — Froude > 0.7 streaks advected downstream; standing
    // waves (Fr > 1) are anchored to the bed, not the flow (Uncharted 4 / HFW).
    if (froude && flowSpeed) {
      // The streaks ride the flow two-phase as well, on their own slower cycle (session 2,
      // 2026-09-25): advected by uTime·speed with a per-vertex speed they sheared into
      // salt-and-pepper within minutes, like the detail layer above. Each phase is masked
      // before the cross-fade so the streak density holds through the blend.
      const fsp = flowSpeed;
      const sCycle = 3.2;
      const sJit = flowJitter ?? mx_noise_float(vec3(uv().x.mul(0.05), uv().y.mul(0.3), 9.1)).mul(0.5);
      const sp0 = uTime.div(sCycle).add(sJit).fract();
      const sp1 = sp0.add(0.5).fract();
      const sW = abs(sp0.mul(2).sub(1));
      const streakAt = (ph: typeof sp0, seed: number) => smoothstep(0.35, 0.75, mx_noise_float(vec3(
        uv().x.sub(fsp.mul(ph).mul(sCycle)).mul(0.3), uv().y.mul(2.6), uTime.mul(0.15).add(seed),
      )).mul(0.5).add(0.5));
      const streak = mix(streakAt(sp0, 0), streakAt(sp1, 5.3), sW);
      const standing = mx_noise_float(vec3(uv().x.mul(0.9), uv().y.mul(1.4), uTime.mul(0.6))).mul(0.5).add(0.5);
      const white = smoothstep(0.7, 1.2, froude).mul(streak).mul(0.85)
        .add(smoothstep(1.0, 1.6, froude).mul(smoothstep(0.45, 0.8, standing)).mul(0.6));
      fieldFoam = max(fieldFoam, clamp(white, 0, 1));
    }
    const whitecaps = max(jacobianFoam, trailFoam.mul(0.8));
    // Surf band from the baked bed depth: foam hugs the beach from ANY view
    // angle, pulsing gently with the (shoaled) wave phase.
    const beachFoam = bedDepth && config.shoreline
      ? float(1).sub(smoothstep(0, config.shoreline.foamDepth, bedDepth)).mul(0.9).mul(bandK)
      : float(0);
    // With a band width the surf is LACE: the band no longer lifts the noise gate (× 0.3 made it a solid sheet).
    const beachGate = bandM > 0 ? 0.08 : 0.3;
    const foamMask = max(clamp(shoreFoam.add(whitecaps).add(crestFoam.mul(0.5)).add(beachFoam).add(wakeFoam), 0, 1)
      .mul(smoothstep(0.2, 0.7, foamNoise.add(whitecaps.mul(0.35)).add(beachFoam.mul(beachGate)).add(wakeFoam.mul(0.5)))), fieldFoam);

    // ── R3: crest + body scattering (GodotOceanWaves' Atlas BSDF): sunlight
    // through a thin backlit crest glows (k1·max(0,H+h0)·(L·−V)⁴·(½−½L·N)³),
    // the body shows its colour where you look into it (k2·(V·N)²), plus a
    // Lambert term — scatter-coloured, sun-coloured, dimmed with the day, and
    // blended by (1 − F) through the Fresnel mix below. Bubbles under a
    // breaking crest scatter too (k4·whitecap density, before the foam mask).
    if (scatterCfg) {
      const H = dispFrag
        ? clamp(dispFrag.dy.div(ampScaleNode.mul(totalAmp)), -1, 1)
        : float(0);
      const L = uSunDirection;
      const back = max(dot(L, viewDir.negate()), 0);
      const thin = float(0.5).sub(dot(L, normal).mul(0.5));
      // §3.4 (surfaces, 2026-09-27): the peak mask reads the CHOP (the compression 1 − J — Sea of Thieves' choppiness mask),
      // not the height alone: a folding crest glows, a swell's broad top keeps (1 − chop) of it (waterLook crestChopFactor).
      const chopK = scatterCfg.chop ? mix(float(1 - scatterCfg.chop), float(1), smoothstep(0.05, 0.55, float(1).sub(jacFrag))) : float(1);
      const crest = max(H.add(scatterCfg.heightBias), 0).mul(chopK).mul(pow(back, 4)).mul(pow(thin, 3)).mul(scatterCfg.crest);
      const body = pow(max(dot(viewDir, normal), 0), 2).mul(scatterCfg.body);
      const lamb = max(dot(L, normal), 0).mul(scatterCfg.lambert);
      const sunUp = smoothstep(-0.04, 0.08, L.y);
      const sc = vec3(scatterCfg.color[0], scatterCfg.color[1], scatterCfg.color[2]);
      const sss = sc.mul(crest.add(body).add(lamb)).mul(sunLightNode()).mul(sunUp).mul(uScatterStrength);
      const bub = vec3(scatterCfg.bubbleColor[0], scatterCfg.bubbleColor[1], scatterCfg.bubbleColor[2]).mul(whitecaps.mul(scatterCfg.bubbles)).mul(uIlluminance);
      underwater = underwater.add(sss).add(bub) as unknown as typeof underwater;
    }

    const water = mix(underwater, skySide, fresnel.mul(0.9)).add(glint);
    const aboveColor = mix(water, color(new THREE.Color(config.foamColor)).mul(uIlluminance), foamMask);

    // ── Underside (camera submerged, looking up): Snell's window ─────────────
    // Incident ray direction camera → fragment; its angle to the vertical
    // decides transmission (window) vs total internal reflection.
    const incident = normalize(positionWorld.sub(cameraPosition));
    const cosUp = clamp(dot(incident, normal), 0, 1);
    // cos θc ≈ 0.66 — a soft band around it avoids a hard window rim.
    const windowMask = smoothstep(0.6, 0.72, cosUp);
    // Inside the window: the raster behind the surface IS the above-water
    // scene from a submerged camera — refract it through the wavy normal.
    // No-copy fallback: the world above ≈ the sky — the PMREM along the ray
    // REFRACTED out through the surface (R6) when the sky bake is bound.
    const flatThrough = color(new THREE.Color(config.skyColor)).mul(uIlluminance).mul(1.06);
    const skyThrough = config.screenSpaceRefraction !== false
      ? adaptiveSceneColor(
          viewportSafeUV(screenUV.add(normal.xz.mul(config.distortionScale * 1.6))),
        ).rgb.mul(1.06)
      : wantEnv
        ? mix(flatThrough, envSample(normalize(refract(incident, normal.negate(), 1.33)) as never, float(0.08) as never).rgb.mul(uEnvIntensity).mul(1.06), uEnvWeight)
        : flatThrough;
    // Beyond the critical angle: TIR — the surface mirrors the dark water
    // body itself (deep color, slightly modulated so it isn't a flat sheet).
    const tir = deep.mul(float(0.55).add(cosUp.mul(0.25)));
    const belowColor = mix(tir, skyThrough, windowMask).add(glint.mul(windowMask));

    return vec4(mix(belowColor, aboveColor, float(frontFacing)), 1);
  })();

  // Water writes depth normally (it's opaque; refraction fakes the see-through).
  // DoubleSide: the underside renders Snell's window for submerged cameras.
  material.transparent = false;
  material.side = THREE.DoubleSide;



  return {
    material,
    uTime,
    uFrameDt,
    uSunDirection,
    uWorldOffset,
    uIlluminance,
    uSkyColor,
    uSunLight,
    uWaveAmpScale,
    uWaveDirRot,
    waveModulationNodes: modNodes,
    uInteractionCenter,
    uFoamJacobian,
    uDetailDrift,
    uRainIntensity,
    reflectorTarget: reflectionSampler ? reflectionSampler.target : null,
    reflectorNode: reflectionSampler,
    setEnvMap(tex: THREE.Texture | null): void {
      envCurrent = tex ?? envPlaceholder;
      for (const n of envNodes) if (n.value !== envCurrent) n.value = envCurrent;
      uEnvWeight.value = tex ? 1 : 0;
    },
    uEnvWeight,
    uEnvIntensity,
    setCloudTexture(tex: THREE.Texture | null): void {
      cloudCurrent = tex ?? cloudPlaceholder;
      for (const n of cloudNodes) if (n.value !== cloudCurrent) n.value = cloudCurrent;
    },
    uCloudOffset,
    uCloudStrength,
    uScatterStrength,
    uNearSeaRect,
    uPlanarWeight,
    setPlanarActive(on: boolean): void {
      if (!reflectionSampler) return;
      (reflectionSampler.reflector as unknown as { updateBeforeType: string }).updateBeforeType = on ? 'frame' : 'none';
    },
    uFoamCenter,
    setFoamFieldTexture(tex: THREE.Texture): void {
      foamFieldCurrent = tex;
      for (const n of foamFieldNodes) if (n.value !== tex) n.value = tex;
    },
    get planarActive(): boolean {
      return !!reflectionSampler && (reflectionSampler.reflector as unknown as { updateBeforeType: string }).updateBeforeType !== 'none';
    },
  };
}
